"use client";

import Link from "next/link";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  User,
  ShieldCheck,
  LogOut,
  Settings,
  LayoutDashboard,
  LogIn,
  UserPlus,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface UserActionsProps {
  isAuthenticated: boolean;
  isAdmin: boolean;
  user: any;
  logout: () => void;
  isMobile?: boolean;
}

export default function UserActions({
  isAuthenticated,
  isAdmin,
  user,
  logout,
  isMobile = false,
}: UserActionsProps) {
  // Only show admin actions for authenticated admin users
  if (isAdmin && isAuthenticated) {
    if (isMobile) {
      return (
        <div className="space-y-3 w-full font-body">
          <div className="flex items-center mb-4 p-4 rounded-lg">
            <div className="w-12 h-12 rounded-full bg-[var(--color-rr-green)]/10 flex items-center justify-center mr-3">
              <ShieldCheck size={22} className="text-[var(--color-rr-green)]" />
            </div>
            <div>
              <p className="font-bold text-xl text-black">{user?.name || "Admin"}</p>
              <p className="text-base text-gray-500">Admin Panel</p>
            </div>
          </div>
          <Link href="/admin">
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 h-12 text-base hover:bg-[var(--color-rr-green)]/10"
            >
              <ShieldCheck size={18} /> Admin Panel
            </Button>
          </Link>
          <Button
            onClick={logout}
            variant="ghost"
            className="w-full justify-start gap-3 h-12 text-base text-red-600 hover:bg-red-50"
          >
            <LogOut size={18} /> Logout
          </Button>
        </div>
      );
    }

    // Desktop admin version
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="gap-2 h-11 px-6 text-base font-body hover:bg-[var(--color-rr-green)]/10 hover:border-[var(--color-rr-green)]">
            <ShieldCheck size={18} />
            {user?.name || "Admin"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60 font-body">
          <DropdownMenuLabel className="text-base">Admin Account</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link href="/admin" className="flex items-center text-base py-3">
              <ShieldCheck size={16} className="mr-3" /> Admin Panel
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={logout}
            className="text-red-600 focus:text-red-700 focus:bg-red-50 text-base py-3"
          >
            <LogOut size={16} className="mr-3" /> Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // No user actions for regular users - direct checkout flow
  return null;
}